import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Plus,
  MagnifyingGlass,
  DotsThree,
  Eye,
  PencilSimple,
  PaperPlaneTilt,
  Funnel,
  DownloadSimple,
  UploadSimple,
  FileArrowUp,
  CheckCircle,
  XCircle,
  SpinnerGap,
  FileText,
} from '@phosphor-icons/react';
import { ExportPreviewModal } from '../components/ExportPreviewModal';
import { fmtINR, fmtUSD } from '../utils/currency';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount, currency = 'INR') => {
  if (currency === 'INR') {
    return `₹${amount?.toLocaleString('en-IN') || 0}`;
  }
  return `$${amount?.toLocaleString('en-US') || 0}`;
};

const getVisaType = (category) => {
  if (!category) return '-';
  if (category.includes('cpt_opt')) return 'CPT/OPT';
  if (category.includes('h1')) return 'H1';
  if (category.includes('gc_usc')) return 'GC/USC';
  if (category.includes('bench')) return 'Bench';
  if (category.includes('c2c')) return 'C2C';
  if (category.includes('fulltime')) return 'Full-Time';
  if (category.includes('internal')) return 'Internal';
  if (category.includes('sales_recruiting_direct')) return 'Direct';
  if (category.includes('core_recruiting')) return 'Core';
  return category.replace(/_/g, ' ');
};

const getStatusBadge = (status) => {
  const styles = {
    draft: 'status-draft',
    submitted: 'status-submitted',
    reviewing: 'status-reviewing',
    pending_completion: 'bg-purple-100 text-purple-700',
    pending_lead_review: 'bg-orange-100 text-orange-700',
    lead_approved: 'bg-teal-100 text-teal-700',
    manager_approved: 'bg-blue-100 text-blue-700',
    pending_manager_review: 'bg-indigo-100 text-indigo-700',
    approved: 'status-approved',
    sa_approved: 'bg-emerald-200 text-emerald-800',
    rejected: 'status-rejected',
    backout: 'bg-rose-100 text-rose-700',
    backed_out: 'bg-rose-100 text-rose-700',
  };
  const labels = {
    draft: 'DRAFT',
    submitted: 'SUBMITTED',
    reviewing: 'REVIEWING',
    pending_completion: 'AWAITING COMPLETION',
    pending_lead_review: 'PENDING REVIEW',
    lead_approved: 'PENDING MANAGER',
    manager_approved: 'PENDING SA',
    pending_manager_review: 'MGR REVIEW',
    approved: 'APPROVED',
    sa_approved: 'PAYMENT CONFIRMED',
    rejected: 'REJECTED',
    backout: 'BACKOUT',
    backed_out: 'BACKED OUT',
  };
  return <Badge className={styles[status] || 'status-draft'}>{labels[status] || status?.toUpperCase()}</Badge>;
};

const PlacementsPage = () => {
  const { user, isRecruiter, isLead, isManager, isSuperadmin, isCeo } = useAuth();
  const navigate = useNavigate();
  const isAdmin = isSuperadmin || isCeo;
  const [placements, setPlacements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('confirmed'); // 'pending_review' or 'confirmed'
  const [moduleFilter, setModuleFilter] = useState('all');
  const [visaFilter, setVisaFilter] = useState('all');
  const [techFilter, setTechFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchDebounce, setSearchDebounce] = useState('');
  const [teamFilter, setTeamFilter] = useState('all');
  const [recruiterFilter, setRecruiterFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filterOptions, setFilterOptions] = useState({ teams: [], leads: [], managers: [], recruiters: [] });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showExport, setShowExport] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkFile, setBulkFile] = useState(null);
  const bulkFileRef = useRef(null);
  const [confirmingId, setConfirmingId] = useState(null);

  useEffect(() => {
    fetchPlacements();
  }, [activeTab, moduleFilter, visaFilter, techFilter, teamFilter, recruiterFilter, searchDebounce, dateFrom, dateTo, page]);

  useEffect(() => {
    const timer = setTimeout(() => { setSearchDebounce(searchQuery); setPage(1); }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (isAdmin) {
      axios.get(`${API_URL}/api/placements/filter-options`, { withCredentials: true })
        .then(res => setFilterOptions(res.data))
        .catch(() => {});
    }
  }, []);

  const fetchPlacements = async () => {
    setLoading(true);
    try {
      // Placements page = SA-finalized records only; in-pipeline items live in Review Queue
      const statusParam = isAdmin && activeTab === 'payment_confirmed' ? 'sa_approved' : 'approved_placements';
      let url = `${API_URL}/api/placements?page=${page}&limit=20&status=${statusParam}`;
      if (moduleFilter !== 'all') url += `&module=${moduleFilter}`;
      if (visaFilter !== 'all') url += `&visa=${encodeURIComponent(visaFilter)}`;
      if (techFilter !== 'all') url += `&technology=${encodeURIComponent(techFilter)}`;
      if (teamFilter !== 'all') url += `&team_name=${encodeURIComponent(teamFilter)}`;
      if (recruiterFilter !== 'all') url += `&created_by=${recruiterFilter}`;
      if (searchDebounce) url += `&search=${encodeURIComponent(searchDebounce)}`;
      if (dateFrom && dateTo) {
        url += `&date_from=${encodeURIComponent(dateFrom)}&date_to=${encodeURIComponent(dateTo)}`;
      }
      const response = await axios.get(url, { withCredentials: true });
      setPlacements(response.data.placements);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to fetch placements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPayment = async (placementId) => {
    setConfirmingId(placementId);
    try {
      await axios.post(`${API_URL}/api/placements/${placementId}/sa-approve`, {}, { withCredentials: true });
      fetchPlacements();
    } catch (error) {
      alert('Failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setConfirmingId(null);
    }
  };

  const filteredPlacements = placements;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 font-['Outfit'] tracking-tight">
            Placements
          </h1>
          <p className="text-slate-500 mt-1">Manage and track your placement submissions. Use filters to narrow down by status, visa type, or technology.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowExport(true)} className="gap-1.5" data-testid="export-placements-btn">
            <DownloadSimple size={16} /> Export to Excel
          </Button>
          {(isSuperadmin || isCeo) && (
            <Button variant="outline" onClick={() => { setBulkResult(null); setBulkFile(null); setShowBulkUpload(true); }} className="gap-1.5" data-testid="bulk-upload-placements-btn">
              <UploadSimple size={16} /> Bulk Upload
            </Button>
          )}
          {(isRecruiter || isLead || isManager || isSuperadmin || isCeo) && (
            <Link to="/dashboard/placements/new">
              <Button className="bg-blue-600 hover:bg-blue-700" data-testid="new-placement-btn">
                <Plus size={20} className="mr-2" />
                New Placement
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Tabs — SA/CEO can filter payment-confirmed vs all approved */}
      {isAdmin && (
        <div className="flex gap-1 border-b border-slate-200">
          <button
            onClick={() => { setActiveTab('confirmed'); setPage(1); }}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-all ${activeTab === 'confirmed' ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            data-testid="tab-confirmed"
          >
            Approved Placements
          </button>
          <button
            onClick={() => { setActiveTab('payment_confirmed'); setPage(1); }}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-all ${activeTab === 'payment_confirmed' ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            data-testid="tab-payment-confirmed"
          >
            Payment Confirmed
          </button>
        </div>
      )}

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search by candidate, vendor, or client..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={moduleFilter} onValueChange={(v) => { setModuleFilter(v); setPage(1); }}>
              <SelectTrigger className="w-full md:w-44" data-testid="module-filter">
                <SelectValue placeholder="All Modules" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Modules</SelectItem>
                <SelectItem value="sales">Bench Sales</SelectItem>
                <SelectItem value="recruiting">Recruiting</SelectItem>
              </SelectContent>
            </Select>
            <Select value={visaFilter} onValueChange={(v) => { setVisaFilter(v); setPage(1); }}>
              <SelectTrigger className="w-full md:w-44" data-testid="visa-filter">
                <SelectValue placeholder="Visa Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Visa Types</SelectItem>
                <SelectItem value="cpt_opt">CPT/OPT</SelectItem>
                <SelectItem value="h1">H1 Transfer</SelectItem>
                <SelectItem value="gc_usc">GC / USC</SelectItem>
                <SelectItem value="bench">Bench</SelectItem>
                <SelectItem value="c2c">C2C</SelectItem>
                <SelectItem value="fulltime">Full-Time</SelectItem>
              </SelectContent>
            </Select>
            <Select value={techFilter} onValueChange={(v) => { setTechFilter(v); setPage(1); }}>
              <SelectTrigger className="w-full md:w-44" data-testid="tech-filter">
                <SelectValue placeholder="Technology" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Technologies</SelectItem>
                <SelectItem value="Semiconductors">Semiconductors</SelectItem>
                <SelectItem value="Automotive">Automotive</SelectItem>
                <SelectItem value="Automation 4.0">Automation 4.0</SelectItem>
                <SelectItem value="Battery">Battery</SelectItem>
                <SelectItem value="AI/ML">AI/ML</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col sm:flex-row flex-wrap items-end gap-3 mt-3 pt-3 border-t border-slate-100">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-slate-500 whitespace-nowrap">Placement date</span>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                className="w-[145px] h-9 text-sm"
                data-testid="placements-date-from"
              />
              <span className="text-slate-400 text-sm">to</span>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                className="w-[145px] h-9 text-sm"
                data-testid="placements-date-to"
              />
            </div>
            {(dateFrom || dateTo) && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-9 text-slate-500"
                onClick={() => { setDateFrom(''); setDateTo(''); setPage(1); }}
                data-testid="placements-date-clear"
              >
                Clear dates
              </Button>
            )}
          </div>
          {/* Team/Recruiter Filters for SA/CEO */}
          {(isSuperadmin || isCeo) && filterOptions.teams.length > 0 && (
            <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-slate-100">
              <Select value={teamFilter} onValueChange={(v) => { setTeamFilter(v); setPage(1); }}>
                <SelectTrigger className="w-full md:w-44" data-testid="team-filter"><SelectValue placeholder="All Teams" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Teams</SelectItem>
                  {filterOptions.teams.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={recruiterFilter} onValueChange={(v) => { setRecruiterFilter(v); setPage(1); }}>
                <SelectTrigger className="w-full md:w-48" data-testid="recruiter-filter"><SelectValue placeholder="All Recruiters" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Recruiters</SelectItem>
                  {filterOptions.recruiters.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Placements Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="skeleton h-8 w-48 mx-auto rounded mb-4" />
              <div className="skeleton h-4 w-32 mx-auto rounded" />
            </div>
          ) : filteredPlacements.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <p>No placements found</p>
              {(isRecruiter || isLead || isManager) && (
                <Link to="/dashboard/placements/new">
                  <Button variant="outline" className="mt-4">
                    Create Your First Placement
                  </Button>
                </Link>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full data-table">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3">Candidate</th>
                    <th className="px-4 py-3">Created By</th>
                    <th className="px-4 py-3">Category</th>
                    <th className="px-4 py-3">Visa Type</th>
                    <th className="px-4 py-3">Technology</th>
                    <th className="px-4 py-3">MSP / Client</th>
                    <th className="px-4 py-3 text-right">Bill Rate</th>
                    <th className="px-4 py-3 text-right">Margin</th>
                    <th className="px-4 py-3">Placement Date</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">My Payout</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPlacements.map((placement) => {
                    const isSpecial = placement.vendor_type === 'new' || placement.vendor_type === 'existing_new' || placement.client_type === 'new';
                    const isLowEnd = placement.is_low_end;
                    return (
                    <tr key={placement.id} className={`${isSpecial ? 'bg-cyan-50/40' : ''} ${isLowEnd ? 'bg-amber-50/40' : ''}`} data-testid={`placement-row-${placement.id}`}>
                      <td className="font-medium">
                        <div className="flex items-center gap-2">
                          <span>{placement.candidate_name}</span>
                          <span className={`inline-flex items-center text-[10px] px-1.5 py-0.5 rounded ${placement.module === 'recruiting' ? 'bg-teal-100 text-teal-700' : 'bg-blue-100 text-blue-700'}`}>
                            {placement.module === 'recruiting' ? 'REC' : 'SAL'}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-1 mt-0.5">
                          {placement.vendor_type === 'new' && (
                            <span className="inline-flex items-center text-[9px] px-1.5 py-0.5 bg-cyan-100 text-cyan-700 rounded font-semibold" data-testid={`new-vendor-badge-${placement.id}`}>NEW VENDOR</span>
                          )}
                          {placement.vendor_type === 'existing_new' && (
                            <span className="inline-flex items-center text-[9px] px-1.5 py-0.5 bg-cyan-50 text-cyan-600 rounded font-semibold border border-cyan-200" data-testid={`exist-new-vendor-badge-${placement.id}`}>EXIST-NEW VENDOR</span>
                          )}
                          {placement.client_type === 'new' && (
                            <span className="inline-flex items-center text-[9px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded font-semibold" data-testid={`new-client-badge-${placement.id}`}>NEW CLIENT</span>
                          )}
                          {isLowEnd && (
                            <span className="inline-flex items-center text-[9px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded font-semibold" data-testid={`low-end-badge-${placement.id}`}>LOW-END</span>
                          )}
                          {placement.is_new_technology && placement.new_technology_name && (
                            <span className="inline-flex items-center text-[9px] px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded font-semibold">
                              {placement.new_technology_name}
                            </span>
                          )}
                          {placement.is_shared_split && (
                            <span className="inline-flex items-center text-[9px] px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded" data-testid="shared-split-badge">
                              Shared by {placement.shared_by} ({placement.share_percentage}%)
                            </span>
                          )}
                          {placement.split_share_info && (
                            <span className="inline-flex items-center text-[9px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded" data-testid="sharing-split-badge">
                              Sharing {placement.split_share_info.percentage}% with {placement.split_share_info.shared_with}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="text-xs text-slate-600">
                        <span className="font-medium" data-testid={`created-by-${placement.id}`}>{placement.created_by_name || '-'}</span>
                      </td>
                      <td className="text-slate-500 text-xs">
                        {placement.category?.replace(/_/g, ' ').toUpperCase()}
                      </td>
                      <td>
                        <Badge className="bg-slate-100 text-slate-600 text-[10px]" data-testid={`visa-${placement.id}`}>
                          {getVisaType(placement.category)}
                        </Badge>
                      </td>
                      <td>
                        {placement.is_new_technology && placement.new_technology_name ? (
                          <Badge className="bg-blue-100 text-blue-700 text-[10px]" data-testid={`tech-${placement.id}`}>
                            {placement.new_technology_name}
                          </Badge>
                        ) : (
                          <span className="text-slate-400 text-xs">-</span>
                        )}
                      </td>
                      <td>
                        <div className="text-sm">{placement.vendor}</div>
                        <div className="text-xs text-slate-400">{placement.client}</div>
                      </td>
                      <td className="text-right text-xs">{fmtUSD(placement.bill_rate)}/hr</td>
                      <td className="text-right font-medium text-xs">{fmtUSD(placement.margin)}/hr</td>
                      <td className="text-slate-600 text-xs whitespace-nowrap">
                        {(placement.start_date || placement.created_at || '').slice(0, 10) || '—'}
                      </td>
                      <td>{getStatusBadge(placement.status)}</td>
                      <td className="text-right font-medium text-emerald-600 text-xs">
                        {fmtINR(
                          placement.my_projected_payout
                          ?? placement.my_split_amount
                          ?? placement.incentives?.my_split
                          ?? placement.incentives?.total_placement
                          ?? 0
                        )}
                      </td>
                      <td className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {isAdmin && placement.status === 'approved' && (
                            <Button
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700 text-xs h-7 px-2"
                              onClick={() => handleConfirmPayment(placement.id)}
                              disabled={confirmingId === placement.id}
                              data-testid={`confirm-payment-${placement.id}`}
                            >
                              {confirmingId === placement.id ? <SpinnerGap size={14} className="animate-spin mr-1" /> : <CheckCircle size={14} className="mr-1" />}
                              Confirm
                            </Button>
                          )}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`actions-btn-${placement.id}`}>
                                <DotsThree size={20} weight="bold" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => navigate(`/dashboard/placements/${placement.id}`)}>
                                <Eye size={16} className="mr-2" />
                                View Details
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {total > 20 && (
            <div className="flex items-center justify-between p-4 border-t border-slate-200">
              <p className="text-sm text-slate-500">
                Showing {(page - 1) * 20 + 1} to {Math.min(page * 20, total)} of {total}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page * 20 >= total}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      <ExportPreviewModal
        open={showExport}
        onClose={() => setShowExport(false)}
        data={filteredPlacements}
        fileName={`Placements_${new Date().toISOString().slice(0,10)}`}
        title="Placements Export Preview"
        columns={[
          { key: 'candidate', header: 'Candidate', accessor: r => r.candidate_name },
          { key: 'category', header: 'Category', accessor: r => r.category?.replace(/_/g, ' ').toUpperCase() },
          { key: 'visa', header: 'Visa Type', accessor: r => getVisaType(r.category) },
          { key: 'vendor', header: 'Vendor', accessor: r => r.vendor },
          { key: 'client', header: 'Client', accessor: r => r.client },
          { key: 'bill_rate', header: 'Bill Rate ($)', accessor: r => r.bill_rate },
          { key: 'pay_rate', header: 'Pay Rate ($)', accessor: r => r.pay_rate },
          { key: 'margin', header: 'Margin ($)', accessor: r => r.margin?.toFixed(2) },
          { key: 'status', header: 'Status', accessor: r => r.status },
          { key: 'payout', header: 'My Payout (INR)', accessor: r => r.my_projected_payout ?? r.my_split_amount ?? r.incentives?.my_split ?? r.incentives?.total_placement ?? 0 },
          { key: 'created', header: 'Date', accessor: r => (r.start_date || r.created_at || '').slice(0, 10) },
        ]}
      />

      {/* Bulk Placement Upload Modal */}
      <Dialog open={showBulkUpload} onOpenChange={(open) => { setShowBulkUpload(open); if (!open) { setBulkFile(null); setBulkResult(null); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Bulk Upload Placements</DialogTitle>
            <DialogDescription>Upload an Excel file to create multiple placements at once. Placements will be created as drafts.</DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-medium text-blue-800 mb-2 flex items-center gap-2"><FileText size={18} />Step 1: Download Template</h4>
              <p className="text-sm text-blue-600 mb-3">Template includes columns: candidate_first_name, candidate_last_name, module, category, company, vendor, client, bill_rate, pay_rate, start_date, placement_month, placement_year, job_title, industry, recruiter_email, organization</p>
              <Button variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-100"
                onClick={async () => {
                  try {
                    const res = await axios.get(`${API_URL}/api/placements/bulk-template`, { withCredentials: true, responseType: 'blob' });
                    const url = window.URL.createObjectURL(new Blob([res.data]));
                    const link = document.createElement('a'); link.href = url;
                    link.setAttribute('download', 'placement_upload_template.xlsx');
                    document.body.appendChild(link); link.click(); link.remove();
                  } catch (err) { alert('Failed to download template'); }
                }}
                data-testid="download-placement-template">
                <DownloadSimple size={16} className="mr-2" /> Download Template (.xlsx)
              </Button>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <h4 className="font-medium text-slate-800 mb-2 flex items-center gap-2"><FileArrowUp size={18} />Step 2: Upload Your File</h4>
              <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${bulkFile ? 'border-emerald-300 bg-emerald-50' : 'border-slate-300 hover:border-blue-400'}`}>
                <input ref={bulkFileRef} type="file" accept=".xlsx" onChange={(e) => { const f = e.target.files[0]; if (f) setBulkFile(f); }} className="hidden" id="bulk-placement-upload" />
                <label htmlFor="bulk-placement-upload" className="cursor-pointer">
                  {bulkFile ? (
                    <div className="flex items-center justify-center gap-3"><CheckCircle size={24} className="text-emerald-600" /><p className="font-medium text-emerald-700">{bulkFile.name}</p></div>
                  ) : (
                    <div><UploadSimple size={32} className="mx-auto mb-2 text-slate-400" /><p className="text-sm text-slate-600">Click to select an XLSX file</p></div>
                  )}
                </label>
              </div>
            </div>
            {bulkResult && (
              <div>{bulkResult.error ? (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg"><XCircle size={20} className="text-red-600 inline mr-2" /><span className="text-red-700">{bulkResult.error}</span></div>
              ) : (
                <div className="space-y-2">
                  <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <CheckCircle size={20} className="text-emerald-600 inline mr-2" />
                    <span className="text-emerald-700 font-medium">{bulkResult.message}</span>
                    <p className="text-xs text-emerald-600 mt-1">{bulkResult.created} of {bulkResult.total_rows} placements created as drafts</p>
                  </div>
                  {bulkResult.errors?.length > 0 && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg max-h-32 overflow-y-auto">
                      <p className="text-xs font-semibold text-amber-700 mb-1">Errors:</p>
                      {bulkResult.errors.map((e, i) => <p key={i} className="text-xs text-amber-600">{e}</p>)}
                    </div>
                  )}
                </div>
              )}</div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => { setShowBulkUpload(false); setBulkFile(null); setBulkResult(null); }}>{bulkResult ? 'Close' : 'Cancel'}</Button>
            {!bulkResult && (
              <Button className="bg-blue-600 hover:bg-blue-700" disabled={bulkLoading || !bulkFile}
                onClick={async () => {
                  if (!bulkFile) return;
                  setBulkLoading(true); setBulkResult(null);
                  try {
                    const fd = new FormData(); fd.append('file', bulkFile);
                    const res = await axios.post(`${API_URL}/api/placements/bulk-upload`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
                    setBulkResult(res.data);
                    fetchPlacements();
                  } catch (err) { setBulkResult({ error: err.response?.data?.detail || err.message }); }
                  finally { setBulkLoading(false); }
                }}
                data-testid="upload-placements-btn">
                {bulkLoading && <SpinnerGap size={16} className="animate-spin mr-2" />} Upload & Create
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlacementsPage;
